/**
 * Offline Cache Layer
 *
 * Provides localStorage-based caching for API GET responses so that
 * the app can display stale data when the network is unavailable.
 *
 * Also includes a mutation queue that stores failed write operations
 * and replays them when the network returns.
 *
 * Cache keys are prefixed with "zap_cache:" to avoid collisions.
 * Mutation queue is stored under "zap_mutation_queue".
 *
 * TTL: cached responses expire after 30 minutes by default.
 */

/* ================================================================
   Response Cache (for GET requests)
   ================================================================ */

const CACHE_PREFIX = "zap_cache:";
const DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
}

/** Store a response in the offline cache */
export function cacheSet<T>(key: string, data: T, ttl = DEFAULT_TTL): void {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), ttl };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — silently fail
    // Try to evict oldest entries to make room
    _evictOldest(3);
    try {
      const entry: CacheEntry<T> = { data, timestamp: Date.now(), ttl };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // Still failed — give up silently
    }
  }
}

/** Retrieve a cached response (returns null if missing or expired) */
export function cacheGet<T = any>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.timestamp > entry.ttl) {
      // Expired — remove and return null
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

/** Retrieve a cached response even if expired (for offline fallback) */
export function cacheGetStale<T = any>(key: string): { data: T; age: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    return { data: entry.data, age: Date.now() - entry.timestamp };
  } catch {
    return null;
  }
}

/** Remove a specific cached entry */
export function cacheRemove(key: string): void {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch { /* ignore */ }
}

/** Clear all offline cache entries */
export function cacheClearAll(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keysToRemove.push(k);
    }
    for (const k of keysToRemove) localStorage.removeItem(k);
  } catch { /* ignore */ }
}

/** Evict the N oldest cache entries to free up space */
function _evictOldest(count: number): void {
  try {
    const entries: Array<{ key: string; ts: number }> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) {
        try {
          const raw = localStorage.getItem(k);
          if (raw) {
            const parsed = JSON.parse(raw);
            entries.push({ key: k, ts: parsed.timestamp || 0 });
          }
        } catch { /* skip */ }
      }
    }
    entries.sort((a, b) => a.ts - b.ts);
    for (let i = 0; i < Math.min(count, entries.length); i++) {
      localStorage.removeItem(entries[i].key);
    }
  } catch { /* ignore */ }
}

/* ================================================================
   Mutation Queue (for offline writes)
   ================================================================ */

const QUEUE_KEY = "zap_mutation_queue";

export interface QueuedMutation {
  id: string;
  path: string;
  method: string;
  body?: string;
  timestamp: number;
  /** Number of replay attempts so far */
  attempts: number;
  /** Human-readable description for the UI */
  description?: string;
}

/** Get all queued mutations */
export function getQueuedMutations(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Add a mutation to the offline queue */
export function enqueueMutation(
  path: string,
  method: string,
  body?: string,
  description?: string,
): void {
  try {
    const queue = getQueuedMutations();
    const id = `mut_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    queue.push({ id, path, method, body, timestamp: Date.now(), attempts: 0, description });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    console.log(`[OfflineQueue] Enqueued mutation: ${method} ${path}`, description || "");
  } catch {
    // Storage full — can't queue
    console.warn("[OfflineQueue] Failed to enqueue mutation — storage may be full");
  }
}

/** Remove a mutation from the queue by id */
export function dequeueMutation(id: string): void {
  try {
    const queue = getQueuedMutations().filter((m) => m.id !== id);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch { /* ignore */ }
}

/** Clear the entire mutation queue */
export function clearMutationQueue(): void {
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch { /* ignore */ }
}

/** Update attempt count for a queued mutation */
export function incrementMutationAttempts(id: string): void {
  try {
    const queue = getQueuedMutations();
    const item = queue.find((m) => m.id === id);
    if (item) {
      item.attempts += 1;
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    }
  } catch { /* ignore */ }
}
